import { Bell, Inbox, Menu, MessagesSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import OpsAloLogo from './logo';
import { useAppColors } from '@/src/lib/theme';

interface TopBarProps {
  venueName: string;
  ownerName: string;
  isScrolled: boolean;
  onOpenLeft: () => void;
  // Managers only: opens the approvals sheet. Omit to hide the button.
  onOpenRight?: () => void;
  // Show the inbox even with a zero count (staff with request history).
  showInboxWhenEmpty?: boolean;
  onOpenSettings?: () => void;
  /** Team Chats panel (top right). */
  onOpenTeam?: () => void;
  teamUnread?: number;
  teamOpen?: boolean;
  onOpenNotifications: () => void;
  pendingCount: number;
  notificationCount: number;
  busyLevel: number;
}

export default function TopBar({
  ownerName,
  isScrolled,
  onOpenLeft,
  onOpenRight,
  showInboxWhenEmpty,
  onOpenTeam,
  teamUnread = 0,
  teamOpen = false,
  onOpenNotifications,
  pendingCount,
  notificationCount,
}: TopBarProps) {
  const c = useAppColors();

  return (
    <header
    className={`fixed top-0 left-0 right-0 h-14 z-40 transition-all duration-300 flex items-center px-4 justify-between safe-top ${
        isScrolled ? 'border-b' : 'border-b border-transparent'
      }`}
      style={{
        background: c.bg,
        borderColor: isScrolled ? c.border : 'transparent',
      }}
      id="top-bar"
    >
      <div className="flex items-center gap-3">
        <motion.button
          data-tour="menu"
          whileTap={{ scale: 0.95 }}
          onClick={onOpenLeft}
          className="lg:hidden p-1 -ml-1 outline-none"
          style={{ color: c.textDim }}
          id="menu-trigger"
        >
          <Menu size={20} />
        </motion.button>
        <motion.div
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-3 cursor-pointer lg:cursor-default"
          onClick={onOpenLeft}
        >
          <OpsAloLogo />
        </motion.div>
      </div>

      <div className="flex items-center gap-1">
        {onOpenTeam && (
          <motion.button id="team-trigger" whileTap={{ scale: 0.95 }} onClick={onOpenTeam} title="Team Chats" aria-label={`Team Chats${teamUnread ? `, ${teamUnread} unread` : ''}`}
            className="relative p-2 outline-none rounded-lg transition-colors" style={{ color: teamOpen ? c.accentSoft : c.textDim, background: teamOpen ? c.accentBg : 'transparent' }}>
            <MessagesSquare size={18} />
            {teamUnread > 0 && (
              <span className="absolute top-1 right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-bold flex items-center justify-center rounded-full" style={{ background: c.accent, color: '#fff' }}>
                {teamUnread > 9 ? '9+' : teamUnread}
              </span>
            )}
          </motion.button>
        )}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onOpenNotifications}
          className="relative p-2 outline-none rounded-lg hover:bg-white/5 transition-colors"
          style={{ color: c.textDim }}
          id="notif-trigger"
        >
          <Bell size={18} />
          <AnimatePresence>
            {notificationCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 text-[10px] font-bold flex items-center justify-center rounded-full"
                style={{ background: c.gold, color: '#0F0F0D' }}
              >
                {notificationCount > 9 ? '9+' : notificationCount}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>

        {onOpenRight && (pendingCount > 0 || showInboxWhenEmpty) && (
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onOpenRight}
          className="relative p-2 outline-none rounded-lg hover:bg-white/5 transition-colors"
          style={{ color: c.textDim }}
          id="inbox-trigger"
          title="Inbox"
          aria-label={`Inbox, ${pendingCount} new`}
        >
          <Inbox size={18} />
          {pendingCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 text-[10px] font-bold flex items-center justify-center rounded-full"
            style={{ background: '#8AE08A', color: '#0F0F0D' }}
          >
            {pendingCount > 9 ? '9+' : pendingCount}
          </motion.span>
          )}
        </motion.button>
        )}

      </div>
    </header>
  );
}